import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AGENCY_ID, AGENCY_CONTACT_EMAIL } from "../../../config/appConfig";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Link } from "react-router-dom";

const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;

export const WidgetMail: React.FC = () => {
    const { t } = useTranslation();
    const [privacyConsent, setPrivacyConsent] = useState(false);
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handlePrivacyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPrivacyConsent(e.target.checked);
    };

    const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const form = e.currentTarget;

        if (!privacyConsent) {
            alert(t('validation.privacyAcceptanceRequired'));
            return;
        }

        setIsSubmitting(true);

        try {
            if (window._iub && window._iub.cons_instructions) {
                const formElement = document.getElementById("contact_form");

                window._iub.cons_instructions.push(["submit", {
                    form: {
                        selector: formElement,
                        map: {
                            subject: {
                                full_name: "info_sender",
                                email: "info_email"
                            },
                            preferences: {
                                privacy_policy: "generic"
                            }
                        },
                        consent: {
                            legal_notices: [{ identifier: "generic" }]
                        }
                    }
                }
                ]);
            } else {
                console.warn("Script Iubenda Consent Solution non trovato (window._iub).");
            }

            if (!executeRecaptcha) {
                console.error("executeRecaptcha non disponibile");
                alert(t('validation.internalErrorRecaptcha'));
                setIsSubmitting(false);
                return;
            }
            const recaptchaToken = await executeRecaptcha('contactForm');
            if (!recaptchaToken) {
                console.warn("Token reCAPTCHA non ottenuto");
                alert(t('validation.recaptchaTokenError'));
                setIsSubmitting(false);
                return;
            }

            const formData = new FormData(form);
            const data = Object.fromEntries(formData.entries());

            data.info_recipient = AGENCY_CONTACT_EMAIL;
            data.agencyId = String(AGENCY_ID);
            data.g_recaptcha_response = recaptchaToken;

            const response = await fetch(`${API_BASE_URL}/send-email-info`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });

            const result = await response.json();

            if (response.ok && result.success) {
                alert(t('statusMessages.messageSentSuccessfully'));
                form.reset();
                setPrivacyConsent(false);
            } else {
                const errorMessage = result.error || t('validation.genericRequestError');
                alert(t('validation.emailSendError', { error: errorMessage }));
            }

        } catch (error) {
            console.error("Errore nella richiesta:", error);
            alert(t('validation.genericRequestError'));
        } finally {
            setIsSubmitting(false);
        }
    }, [t, privacyConsent, executeRecaptcha]);

    return (
        <div className="contact-form">

            <div id="contact-message"></div>

            <form
                id="contact_form"
                className=""
                action=""
                onSubmit={handleSubmit}
                method="POST"
                name="contact_form"
                acceptCharset="utf-8"
            >
                <div className="form-row">

                    <div className="form-group col-md-12">
                        <input
                            type="text"
                            id="info_sender"
                            name="info_sender"
                            className="form-control"
                            placeholder={t('contactForm.nameSurname') || ''}
                            required
                        />
                    </div>

                    <div className="form-group col-md-6">
                        <input
                            type="email"
                            id="info_email"
                            name="info_email"
                            className="form-control"
                            placeholder={t('common.email') || "Email"}
                            required
                            pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                            title={t('contactForm.emailPatternTitle') || "Inserisci un indirizzo email valido, es. esempio@dominio.com"}
                        />
                    </div>

                    <div className="form-group col-md-6">
                        <input
                            type="text"
                            id="info_tel"
                            name="info_tel"
                            className="form-control"
                            placeholder={t('common.telephone') || "Telefono"}
                            required
                            pattern="^\+?(\d[\d-. ]+)?(\([\d-. ]+\))?[\d-. ]+\d$"
                            title={t('contactForm.phonePatternTitle') || "Inserisci un numero di telefono corretto, es. +39 345 678 9999"}
                        />
                    </div>

                    <div className="form-group col-md-12">
                        <textarea
                            id="input_message"
                            name="info_message"
                            cols={50} rows={5}
                            className="form-control"
                            placeholder={`${t('contactForm.message') || "".toString()}...`}
                        ></textarea>
                    </div>

                    <div className="form-group col-md-12">
                        <div className="custom-control custom-checkbox">
                            <input
                                type="checkbox"
                                id="info_privacy"
                                name="generic"
                                value="1"
                                className="custom-control-input"
                                required
                                checked={privacyConsent}
                                onChange={handlePrivacyChange}
                            />
                            <label className="custom-control-label" htmlFor="info_privacy">
                                {t('contactForm.acceptThe') || "Accetto l'"}
                                <Link to="/privacy" title={t('privacyPolicy.title') || "Privacy Policy"}>
                                    {t('contactForm.privacyPolicyLink') || "informativa sulla Privacy"}
                                </Link>
                            </label>
                        </div>
                    </div>

                    <div className="col-md-12">
                        <button
                            id="contact-submit"
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSubmitting}
                        >
                            <span>
                                {isSubmitting ? t('common.sending') || "Invio..." : t('contactForm.sendMessage') || "Invia messaggio"}
                            </span>
                        </button>
                    </div>
                </div>
            </form>
        </div>
    )
}